/**
 * Phase 41 (Plan 41-03 Task 2) — v7 TopBar.
 *
 * Right-rail mounts: optional Search input + "+ Log hours" button +
 * BellDropdown. Left side renders breadcrumbs (optional) + page title +
 * caller-supplied action slot. Pages compose this directly in their JSX
 * when they need title/breadcrumb control; otherwise Layout.tsx mounts a
 * default instance with a path-derived title.
 *
 * Quick task 260512-sidebar-act-as-user (2026-05-12) — UserMenu moved back
 * to Sidebar bottom-left (pre-Phase-41 pattern). TopBar-mounted UserMenu
 * was failing to open its dropdown on staging; restoring the original
 * placement also avoids duplication.
 *
 * Visual port from .planning/v7-design-handoff/v7-sla/shell.jsx lines 179-199.
 *
 * Plan 41-16 (D-Dashboard-layout-v3 amendment 2026-05-12) — Search input +
 * "+ Log hours" button added to the right rail. Both are opt-in via
 * `showSearch` / `showLogHours` props so non-dashboard pages stay lean.
 */
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BellDropdown } from './BellDropdown';
import { Btn } from './Btn';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ThemeEmoji } from './ThemeEmoji';

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface TopBarProps {
  title: string;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
  showSearch?: boolean;
  showLogHours?: boolean;
}

export function TopBar({
  title,
  breadcrumbs,
  actions,
  showSearch = false,
  showLogHours = false,
}: TopBarProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  return (
    <div className="h-14 px-6 bg-ms-surface border-b border-ms-border flex items-center gap-4 shrink-0">
      <div className="flex-1 min-w-0">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="flex items-center gap-1.5 text-[11px] text-ms-text-muted mb-0.5">
            {breadcrumbs.map((b, i) => (
              <span key={`${b.label}-${i}`} className="flex items-center gap-1.5">
                {i > 0 && <span>/</span>}
                {b.href ? (
                  <Link to={b.href} className="hover:text-ms-text-strong">
                    {b.label}
                  </Link>
                ) : (
                  <span className="text-ms-text-strong font-medium">{b.label}</span>
                )}
              </span>
            ))}
          </div>
        )}
        {/* MKS-493 — this div IS the page heading (an H1 in everything but
            the tag), so it carries the brand emoji and the `ms-page-title`
            hook that gives Sparkle its Plus Jakarta Sans 700 / 26px. */}
        <div className="ms-page-title text-[15px] font-semibold text-ms-text-strong truncate">
          {title}
          <ThemeEmoji role="brand" />
        </div>
      </div>
      {showSearch && (
        <div className="relative hidden md:block">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 text-ms-text-muted absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            aria-label="Search"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64 pl-7 pr-3 py-1.5 text-[12px] bg-white border border-ms-border rounded-md text-ms-text-strong placeholder:text-ms-text-muted focus:outline-none focus:ring-2 focus:ring-ms-orange/30 focus:border-ms-orange/40"
          />
        </div>
      )}
      {showLogHours && (
        <Btn variant="accent" size="md" onClick={() => navigate('/time')}>
          + Log hours
        </Btn>
      )}
      {actions}
      <BellDropdown />
    </div>
  );
}
